"use client";

import { Dispatch, SetStateAction, useState } from "react";
import { ColumnDef, ColumnFilter, ColumnFiltersState, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@repo/common/utils/cn";
type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  columnFilters?: ColumnFilter[];
  setColumnFilters?: Dispatch<SetStateAction<ColumnFiltersState>>;
  onRowClick?: (row: TData) => void;
};
export function DataTable<TData, TValue>({
  columns,
  data,
  columnFilters,
  setColumnFilters,
  onRowClick
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters
    }
  });
  return <Table className={"rounded-md border border-gray-100 w-full"}>
      <TableHeader className="bg-gray-100 hidden md:table-header-group">
        {table.getHeaderGroups().map(headerGroup => <TableRow key={headerGroup.id} className="border-gray-100">
            {headerGroup.headers.map(header => {
          return <TableHead key={header.id} className={cn("[&:has(button:not([role='checkbox']))]:px-2", (header.column.columnDef.meta as {
            headerClassName?: string;
          })?.headerClassName)}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>;
        })}
          </TableRow>)}
      </TableHeader>

      <TableBody>
        {table.getRowModel().rows?.length ? table.getRowModel().rows.map((row, index) => <TableRow key={row.id} data-state={row.getIsSelected() && "selected"} className={cn("group/row border-gray-100 [&:has(:hover:where(button))]:hover:bg-transparent", index % 2 === 0 ? "" : "bg-gray-50", onRowClick && "cursor-pointer hover:bg-blue-100 focus:bg-blue-100")} tabIndex={onRowClick ? 0 : -1} onClick={() => onRowClick?.(row.original)} onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          onRowClick?.(row.original);
        }
      }}>
              {row.getVisibleCells().map((cell, cellIndex) => <TableCell key={cell.id} className={cn(cellIndex === 0 ? "w-[1px] whitespace-nowrap px-4 md:pl-4" : "", (cell.column.columnDef.meta as {
          cellClassName?: string;
        })?.cellClassName)}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>)}
            </TableRow>) : <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center">
              No results.
            </TableCell>
          </TableRow>}
      </TableBody>
    </Table>;
}